import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Camera, Upload, ChevronLeft, ChevronRight, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { TryOnCanvas } from "@/components/TryOnCanvas";
import { FaceTryOnCamera } from "@/components/FaceTryOnCamera";

const resolveUrl = (u) => (u && u.startsWith("/api") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function TryOn() {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [mode, setMode] = useState("camera");
  const [photo, setPhoto] = useState(null);
  const [product, setProduct] = useState(null);
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    if (productId) {
      api.get(`/products/${productId}`).then((res) => setProduct(res.data)).catch(() => {});
    }
  }, [productId]);

  useEffect(() => {
    if (source === "wishlist") {
      api.get("/wishlist")
        .then((res) => setCandidates((res.data || []).filter((p) => p.try_on_asset_url)))
        .catch(() => setCandidates([]));
      return;
    }
    api.get("/products", { params: { limit: 12 } })
      .then((res) => setCandidates((res.data.items || []).filter((p) => p.try_on_asset_url)))
      .catch(() => {});
  }, [source]);

  useEffect(() => {
    if (!product && !productId && candidates.length) setProduct(candidates[0]);
  }, [candidates, product, productId]);

  const reelIndex = candidates.findIndex((p) => p.id === product?.id);

  const step = (delta) => {
    if (!candidates.length) return;
    const base = reelIndex === -1 ? 0 : reelIndex;
    const next = (base + delta + candidates.length) % candidates.length;
    setProduct(candidates[next]);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const clearSession = () => {
    setPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const ModeTabs = () => (
    <div className="flex justify-center gap-2 mb-8" data-testid="tryon-mode-tabs">
      <button
        onClick={() => setMode("camera")}
        className={`flex items-center gap-2 px-5 py-2.5 text-xs font-heading font-bold uppercase tracking-wide transition-colors ${mode === "camera" ? "bg-teal-600 text-white" : "border border-zinc-200 text-zinc-600 hover:border-charcoal hover:text-charcoal"}`}
        data-testid="tryon-mode-camera-tab"
      >
        <Camera size={14} /> Live Camera
      </button>
      <button
        onClick={() => setMode("photo")}
        className={`flex items-center gap-2 px-5 py-2.5 text-xs font-heading font-bold uppercase tracking-wide transition-colors ${mode === "photo" ? "bg-teal-600 text-white" : "border border-zinc-200 text-zinc-600 hover:border-charcoal hover:text-charcoal"}`}
        data-testid="tryon-mode-photo-tab"
      >
        <Upload size={14} /> Upload Photo
      </button>
    </div>
  );

  const FrameReel = () => {
    if (!candidates.length) return null;
    const isWishlist = source === "wishlist";
    return (
      <div className="mt-10 max-w-3xl mx-auto" data-testid="tryon-frame-reel">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-xs font-bold uppercase tracking-wide text-teal-700 flex items-center gap-1.5">
            {isWishlist && <Heart size={12} className="fill-rose-500 text-rose-500" />}
            {isWishlist ? `Your Saved Frames · ${candidates.length}` : product ? "Try Another Pair" : "Choose A Pair"}
          </h2>
          {product && (
            <div className="flex gap-2">
              <button onClick={() => step(-1)} className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-charcoal hover:text-charcoal transition-colors" aria-label="Previous frame" data-testid="tryon-reel-prev">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => step(1)} className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center text-zinc-600 hover:border-charcoal hover:text-charcoal transition-colors" aria-label="Next frame" data-testid="tryon-reel-next">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {candidates.map((p) => (
            <button
              key={p.id}
              onClick={() => setProduct(p)}
              className={`shrink-0 w-24 bg-white border rounded-xl p-2 transition-colors ${p.id === product?.id ? "border-teal-600 ring-1 ring-teal-600/30" : "border-zinc-200 hover:border-charcoal"}`}
              data-testid={`tryon-candidate-${p.id}`}
            >
              <img src={p.images?.[0]?.url} alt={p.model} className="w-full aspect-square object-contain mb-1" loading="lazy" />
              <div className="text-[10px] font-heading font-bold uppercase tracking-wide truncate text-zinc-800">{p.brand_name}</div>
            </button>
          ))}
        </div>
        {isWishlist && (
          <p className="text-xs text-zinc-400 mt-2">
            Flick through everything you've saved without leaving this page.{" "}
            <Link to="/account/wishlist" className="underline">Back to wishlist</Link>
          </p>
        )}
      </div>
    );
  };

  const ProductBadge = () =>
    product && (
      <div className="text-center mt-6">
        <div className="font-heading text-sm uppercase tracking-wide text-zinc-900">{product.brand_name} {product.model}</div>
        <div className="text-sm text-zinc-500">{money(product.selling_price)}</div>
        <div className="flex justify-center gap-3 mt-4">
          <button onClick={() => navigate(`/product/${product.slug}`)} className="border border-zinc-200 hover:border-charcoal hover:text-charcoal text-zinc-800 px-5 py-2.5 text-xs font-heading font-bold uppercase transition-colors" data-testid="tryon-view-product">
            View Product
          </button>
          <button onClick={() => step(1)} className="bg-teal-600 hover:bg-charcoal text-white px-5 py-2.5 text-xs font-heading font-bold uppercase transition-colors" data-testid="tryon-try-another-button">
            Next Pair
          </button>
        </div>
      </div>
    );

  return (
    <div className="container-lookers py-10" data-testid="tryon-workspace-page">
      <div className="text-center mb-2">
        <h1 className="font-heading font-semibold uppercase text-4xl text-zinc-950">
          Try Them On
        </h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-md mx-auto">Use your live camera for a real-time mirror, or upload a selfie. Either way the frames are placed on your face automatically and sized to their real measurements.</p>
      </div>

      <ModeTabs />

      {mode === "camera" ? (
        product ? (
          <FaceTryOnCamera glassesUrl={resolveUrl(product.try_on_asset_url)} product={product}>
            <ProductBadge />
          </FaceTryOnCamera>
        ) : (
          <div className="text-center text-sm text-zinc-400 mb-2">Choose a pair below to start your live try-on.</div>
        )
      ) : (
        <>
          {!photo ? (
            <div className="max-w-lg mx-auto text-center" data-testid="tryon-upload-page">
              <div className="border-2 border-dashed border-zinc-200 rounded-sm p-12 mb-4 bg-white">
                <input ref={fileRef} type="file" accept="image/*" capture="user" onChange={onFile} className="hidden" id="tryon-file-input" data-testid="tryon-file-input" />
                <label htmlFor="tryon-file-input" className="cursor-pointer font-heading text-xs uppercase tracking-wide underline text-teal-700">
                  Upload or take a photo
                </label>
                <p className="text-xs text-zinc-400 mt-3">Front-facing, looking directly at the camera, in good lighting works best.</p>
              </div>
              <p className="text-xs text-zinc-400">Your photo stays in your browser — it is never uploaded to Lookers.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-end max-w-md mx-auto mb-4">
                <button onClick={clearSession} className="text-xs underline text-zinc-400" data-testid="tryon-delete-photo">Delete photo &amp; start over</button>
              </div>
              {product ? (
                <>
                  <TryOnCanvas photoUrl={photo} glassesUrl={resolveUrl(product.try_on_asset_url)} product={product} />
                  <ProductBadge />
                </>
              ) : (
                <div className="text-center text-sm text-zinc-400 mb-6">Choose a pair below to try on.</div>
              )}
            </>
          )}
        </>
      )}

      <FrameReel />
    </div>
  );
}
